import { request } from '@umijs/max';
import type {
  IAnalysisItem,
  IChatMatchItem,
  IMatchItem,
  IOcrTaskDetail,
  IRecommendTemplate,
  ITaskDetail,
} from './type';
import { REQUEST_METHODS } from '@/constants';

export const getTaskDetail = (taskCode: string, signal?: AbortSignal) => {
  return request<IRes<ITaskDetail>>(`/supertext-java/tasks/${taskCode}`, {
    signal,
    params: {
      with_batch_tasks: 1,
    },
  });
};

export const getOcrTaskDetail = (taskCode: string, signal?: AbortSignal) => {
  return request<IRes<IOcrTaskDetail>>(`/supertext-java/tasks/${taskCode}`, {
    signal,
  });
};

export const getAnalysisResult = (taskCode: string, signal?: AbortSignal) => {
  return request<IRes<IAnalysisItem[]>>(
    `/supertext-java/tasks/${taskCode}/overall`,
    { ignoreError: true, signal },
  );
};

/**
 * getRecommendTemplate 获取推荐模板
 * @param keyword
 * @param signal
 * @returns
 */
export const getRecommendTemplate = (keyword: string, signal?: AbortSignal) => {
  return request<IRes<IList<IRecommendTemplate>>>(
    '/supertext-java/templates/recommend',
    {
      params: {
        keyword,
      },
      signal,
    },
  );
};

export const getTemplateList = (
  templateCodes: string[],
  signal?: AbortSignal,
) => {
  return request<IRes<IList<IRecommendTemplate>>>('/supertext-java/templates', {
    params: {
      template_codes: templateCodes.join(','),
    },
    signal,
  });
};

/**
 * createChat 创建chat对话
 * @param batchId
 */
export const createChat = (batchId: number) => {
  return request<IRes<{ knowledge_base_id: string }>>(
    '/supertext-java/chat/upload_file',
    {
      method: 'POST',
      params: {
        batch_id: batchId,
      },
    },
  );
};

/**
 * chat 对话接口
 * @param baseId
 * @param text
 * @returns
 */
export function chat(baseId: string, text: string) {
  return request<
    IRes<{
      knowledge_base_id: string;
      result: string;
      match_list: IChatMatchItem[];
    }>
  >('/supertext-java/chat/llm_chat', {
    method: 'POST',
    data: {
      knowledge_base_id: baseId,
      text,
    },
    timeout: 120000,
  });
}

export const downloadOriginalFile = (taskCode: string) => {
  return request<IRes<string>>(`/supertext-java/tasks/${taskCode}/original`);
};
export const downloadTaskResult = (taskCode: string) => {
  return request<IRes<string>>(`/supertext-java/tasks/${taskCode}/result`);
};
export const downloadHighlightFile = (taskCode: string) => {
  return request<IRes<string>>(`/supertext-java/tasks/${taskCode}/highlight`);
};

// 抽取操作
export const changeStatus = (data: {
  taskCode: string;
  payload: {
    tag_codes: string[];
    // 0 取消 1 确认 2 忽略
    status: 0 | 1 | 2;
  };
}) => {
  const { taskCode, payload } = data;
  return request<IRes<any>>(
    `/supertext-py/api/keyword/tasks/${taskCode}/confirm_status/`,
    {
      method: 'PUT',
      data: payload,
    },
  );
};

/**
 * changeStatusForNlpExtraction 抽取操作
 */
export const changeStatusForNlpExtraction = (data: {
  taskCode: string;
  payload: {
    tag_codes?: string[];
    table_codes?: string[];
    task_code: string;
    type: 'IGNORED' | 'NOTICE' | 'CONFIRMED' | 'UNCONFIRMED';
  };
}) => {
  const { taskCode, payload } = data;
  return request<IRes<any>>(`/supertext-java/extraction/task/result/feedback`, {
    method: 'PUT',
    data: payload,
  });
};

export const edit = (data: {
  taskCode: string;
  payload: {
    tag_code: string;
    ki_id: string;
    match: string;
    task_code?: string;
  };
}) => {
  const { taskCode, payload } = data;
  return request(`/supertext-py/api/keyword/tasks/${taskCode}/edition/`, {
    method: 'PUT',
    data: payload,
  });
};
/**
 * 4.5.4 迭代 （抽取模板）
 * @version 4.5.4
 * 删除文本tag
 * **/
export const deleteTextTag = (data : {
  task_code: string;
  tag_code: string;
  ki_id: string;
}) => {
  return request(`/supertext-java/tasks/delete-result`, {
    method: REQUEST_METHODS.post,
    data: data,
  });
};

/**
 * 4.5.4 迭代 （抽取模板）
 * @version 4.5.4
 * 自定义模板新增文本/表格tag
 * **/
export const addTagResult = (data: {
  start?: number;
  end?: number;
  row?: number;
  task_code: string;
  tag_code: string;
  match: string;
}) => {
  return request(`/supertext-java/tasks/add-result`, {
    method: REQUEST_METHODS.post,
    data: data,
  });
};

/**
 * 4.5.4 迭代 （抽取模板）
 * @version 4.5.4
 * 内置模板编辑文本tag
 * **/
export const editBuiltinTextTag = (data: {
  ki_id: string;
  match: string;
  tag_code: string;
  task_code: string;
}) => {
  return request(`/supertext-java/tasks/update-result`, {
    method: REQUEST_METHODS.post,
    data: data,
  });
};

/**
 * 4.4.0 迭代 （画布新增）
 * EXTRACTION抽取
 * @version 4.5.0
 * 增加 手写体识别,印章识别
 */
export const editForNlpExtraction = (data: {
  task_code: string;
  tag_code: string;
  ki_id: string;
  match: string;
  seal_tag?: {
    color: string;
    shape: string;
  };
}) => {
  return request(`/supertext-java/extraction/task/result/edit`, {
    method: 'PUT',
    data: data,
  });
};
/**
 * 4.5.2迭代 （画布新增）
 * @version 4.5.2
 * 抽取结果新增支持 文本字段和表格字段
 * 表格单元格里空白的是没有抽取到，没法编辑，只能新增，需要调用抽取结果添加接口：
 * https://app.apifox.com/link/project/2651026/apis/api-238471920
 */
export const addExtractionPoint = (data: {
  task_code: string;
  tag_code: string;
  ki_id: string;
  match: string;
  seal_tag?: {
    color: string;
    shape: string;
  };
}) => {
  return request(`/supertext-java/extraction/task/result`, {
    method: 'POST',
    data: data,
  });
};
export const addExtractContent = (data: {
  taskCode: string;
  payload: {
    task_id: string;
    tag_code: string;
    start: number;
    end: number;
  };
}) => {
  const { taskCode, payload } = data;
  return request<IRes<IMatchItem>>(
    `/supertext-py/api/keyword/tasks/${taskCode}/keyword_match/`,
    {
      method: 'POST',
      data: payload,
    },
  );
};

/**
 * 4.4.0 迭代
 * EXTRACTION抽取
 */
export const addExtractContentForNlpExtraction = (data: {
  taskCode: string;
  payload: {
    task_id: string;
    tag_code: string;
    start: number;
    end: number;
  };
}) => {
  const { taskCode, payload } = data;
  return request<IRes<IMatchItem>>(`/supertext-java/extraction/task/result`, {
    method: 'POST',
    data: payload,
  });
};
// 审核操作
export const changeReviewStatus = (data: {
  taskCode: string;
  payload: {
    review_fields: string[];
    status: 0 | 1 | 2;
  };
}) => {
  const { taskCode, payload } = data;
  return request<IRes<any>>(
    `/supertext-py/api/review/tasks/${taskCode}/approval/`,
    {
      method: 'PUT',
      data: payload,
    },
  );
};
export const reviewEvaluate = (data: {
  taskCode: string;
  payload: {
    review_field: string;
    /** 0 取消踩 1 踩 */
    is_error: number;
  };
}) => {
  const { taskCode, payload } = data;
  return request<IRes<any>>(
    `/supertext-py/api/review/tasks/${taskCode}/evaluation/`,
    {
      method: 'PUT',
      data: payload,
    },
  );
};
export const addReviewContent = (data: {
  taskCode: string;
  payload: {
    task_id: string;
    tag_code: string;
    start: number;
    end: number;
  };
}) => {
  const { taskCode, payload } = data;
  return request<IRes<IMatchItem>>(
    `/supertext-py/api/review/tasks/${taskCode}/keyword_match/`,
    {
      method: 'POST',
      data: payload,
    },
  );
};
export const editReview = (data: {
  taskCode: string;
  payload: {
    review_field: string;
    keywords?: Array<{
      tag_code: string;
      change_list: Array<
        | {
            ki_id: string;
            change_op: 0;
          }
        | {
            ki_id: string;
            new_match: string;
            change_op: 1;
          }
      >;
    }>;
    modify_suggest?: string;
    risk_explanation?: string;
    legal_provisions?: string;
  };
}) => {
  const { taskCode, payload } = data;
  return request(`/supertext-py/api/review/tasks/${taskCode}/edition/`, {
    method: 'PUT',
    data: payload,
  });
};

/**
 * 4.4.0 用于删除表格列
 */
export const deleteExtractionTag = (data: {
  task_code: string;
  tag_code: string;
  ki_id?: string;
}) => {
  return request(`/supertext-java/extraction/task/result/remove/tag`, {
    method: 'POST',
    data: data,
  });
};

export type * from './type';
